import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Text } from "components/general/Text";
import CookieIcon from "../../../../img/cookie.svg?react";
import React from "react";
import { useCookies } from "react-cookie";
import { Button, ButtonBorderRadius } from "components/general/Button/Button";
import { useIsMobile } from "hooks/useDeviceType";
import { Z_INDICES } from "config/consts";
import { GlobalStore } from "../../../../store/global/GlobalStore";

const COOKIE_KEY = "acceptcookies";
const ONE_YEAR_MS = 31536000;

export const CookieBanner: React.FC = () => {
  const [cookies, setCookie, removeCookie] = useCookies();
  const theme = GlobalStore.useState(s => s.theme);
  const allowCookies = () => {
    setCookie(COOKIE_KEY, true, {
      path: "/",
      maxAge: new Date(ONE_YEAR_MS).getTime(), // 1 Year
    });
  };

  const declineCookies = () => {
    setCookie(COOKIE_KEY, false, {
      path: "/",
      maxAge: new Date(ONE_YEAR_MS).getTime(), // 1 Year
    });
    const bebopCookies = ["termsandconditions", "tokenlistbanner"];
    bebopCookies.forEach(element => {
      removeCookie(element, {
        path: "/",
      });
    });
  };

  if (COOKIE_KEY in cookies) {
    return null;
  }

  return (
    <Container className="bottom-0 end-0">
      <Content>
        <CookieIcon width={64} height={64} />
        <Title>We use cookies.</Title>
        <Text
          as="p"
          textStyle="Paragraph"
          size="Large"
          textCss={css`
            text-align: center;
            line-height: 1.5;
            font-weight: 300;
          `}
        >
          We use cookies to ensure that we give you the best experience on our website. For more
          information, please read our{" "}
          <a href="/privacy.pdf" target="_blank" className="text-link text-decoration-underline">
            Privacy Policy
          </a>
          .
        </Text>
      </Content>
      <ButtonsContainer>
        <Button
          buttonType="primary"
          text="Accept"
          onClick={allowCookies}
          data-testid="cookie-success-btn"
          buttonCss={css`
            padding: 19px 18px;
            justify-content: center;
            width: 150px;
            border-radius: ${ButtonBorderRadius.Mid};
          `}
        />
        <Button
          buttonType="tertiary"
          text="Decline"
          buttonCss={css`
            background-color: ${theme.surface.alt.secondary.default};
            padding: 19px 18px;
            justify-content: center;
            width: 150px;
            border-radius: ${ButtonBorderRadius.Mid};
          `}
          onClick={declineCookies}
        />
      </ButtonsContainer>
    </Container>
  );
};

// Sub-components

const Container = styled.div(({ theme }) => {
  const isMobile = useIsMobile();
  return {
    boxShadow: "0px 0px 8px 0px rgba(0, 0, 0, 0.5), 0px 1px 15px 0px rgba(0, 0, 0, 0.25)",
    backgroundColor: theme.surface.main.primary.default,
    color: "white",
    position: "fixed",
    padding: "36px",
    borderRadius: 36,
    maxWidth: "min(392px, 100%)",
    zIndex: Z_INDICES.POP_UPS,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    ...(isMobile
      ? { marginBottom: 10, marginRight: 10, marginLeft: 10, left: 0 }
      : { marginBottom: 20, marginRight: 20 }),
  };
});

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h5`
  font-family: Quincy CF;
  font-size: 30px;
  font-weight: 800;
  line-height: 111%;
  letter-spacing: -1.2px;
  text-align: center;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-between;
`;
